/**
 * What has been changed about this game (H18).
 *
 * A host can turn a game's variants on and off, and everybody else finds out
 * by losing a trick to a rule they did not know was in play. Somebody who
 * joined late never saw the lobby at all.
 *
 * The summary is deliberately short enough to sit in a chip beside the game's
 * name, because a list nobody reads is the same as no list - the full controls
 * are in the host sheet for anyone who wants them.
 */

import type { VariantSpecWire } from '../net/session.js';

/** A variant's value as a person would read it, or null if it is the default. */
function changed(
  spec: VariantSpecWire,
  value: boolean | string | number | undefined,
): string | null {
  const chosen = value ?? spec.default;
  if (chosen === spec.default) return null;

  /*
   * A toggle's label already reads as the rule: "Jacks skip", "Stack draws".
   * So an ON toggle is its own name, and an OFF one has to say it is off -
   * the label alone would claim the opposite of what is true.
   */
  if (spec.kind === 'toggle') return chosen === true ? spec.label : `No ${spec.label.toLowerCase()}`;

  // A choice names the option rather than the setting: "Jokers: 2" is worse
  // than "2 jokers" only in that nobody says the first one out loud.
  if (spec.kind === 'choice') {
    const option = spec.options?.find((entry) => entry.value === String(chosen));
    return `${spec.label}: ${option?.label ?? String(chosen)}`;
  }

  return `${spec.label}: ${String(chosen)}`;
}

/**
 * One line describing the house rules in play.
 *
 * Names them while there are few enough to read, and counts them after that.
 * Three short phrases in a chip is a glance; six is a paragraph in the wrong
 * place, and the number is the part that makes somebody go and look.
 */
export function houseRules(
  specs: readonly VariantSpecWire[],
  variants: Record<string, boolean | string | number>,
): string {
  const active: string[] = [];
  for (const spec of specs) {
    const text = changed(spec, variants[spec.id]);
    if (text !== null) active.push(text);
  }

  if (active.length === 0) return 'Normal rules';
  if (active.length <= 2) return active.join(' · ');
  return `${String(active.length)} house rules`;
}
